
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { format, addMonths } from "date-fns";
import { CalendarIcon, PlusCircle, Loader2 } from "lucide-react";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { createPaymentSchedule } from "@/services/paymentSchedulesService";

interface SplitPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientName?: string; // Made optional
  /** Abonnement sur lequel porte l'échéancier. Sans lui, rien n'est enregistrable. */
  subscriptionId?: string;
  totalAmount?: number; // Made optional
  onComplete: (schedule: PaymentScheduleItem[]) => void;
}

interface PaymentScheduleItem {
  installmentNumber: number;
  dueDate: Date;
  amount: number;
}

// Create form schema
const splitPaymentSchema = z.object({
  frequency: z.enum(["monthly", "quarterly"]),
  numberOfPayments: z.coerce.number().min(2).max(12),
  firstPaymentDate: z.date(),
});

type SplitPaymentFormValues = z.infer<typeof splitPaymentSchema>;

export const SplitPaymentDialog: React.FC<SplitPaymentDialogProps> = ({
  open,
  onOpenChange,
  clientName = "Client", // Default value
  subscriptionId,
  totalAmount,
  onComplete
}) => {
  const { t } = useTranslation("payments");
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentScheduleItem[]>([]);

  // Le repli `|| 100` de l'appelant faisait porter l'échéancier sur 100 DT
  // quand aucun abonnement n'était sélectionné. On préfère un montant nul et
  // un formulaire désactivé à un montant inventé.
  const amountToSplit = Number(totalAmount ?? 0);
  const canSubmit = Boolean(subscriptionId) && amountToSplit > 0;

  const form = useForm<SplitPaymentFormValues>({
    resolver: zodResolver(splitPaymentSchema),
    defaultValues: {
      frequency: "monthly",
      numberOfPayments: 3,
      firstPaymentDate: new Date(),
    },
  });
  
  const calculateSchedule = React.useCallback(() => {
    const values = form.getValues();
    const { frequency, numberOfPayments, firstPaymentDate } = values;

    if (!numberOfPayments || !firstPaymentDate) {
      setPaymentSchedule([]);
      return;
    }

    const installmentAmount = Math.round((amountToSplit / numberOfPayments) * 100) / 100;
    const schedule: PaymentScheduleItem[] = [];

    for (let i = 0; i < numberOfPayments; i++) {
      let dueDate: Date;

      if (frequency === "monthly") {
        dueDate = addMonths(firstPaymentDate, i);
      } else { // quarterly
        dueDate = addMonths(firstPaymentDate, i * 3);
      }

      schedule.push({
        installmentNumber: i + 1,
        dueDate,
        amount: i === numberOfPayments - 1
          // Le dernier versement absorbe l'arrondi pour que la somme des
          // échéances retombe exactement sur le montant de l'abonnement.
          ? Math.round((amountToSplit - installmentAmount * (numberOfPayments - 1)) * 100) / 100
          : installmentAmount
      });
    }

    setPaymentSchedule(schedule);
  }, [form, amountToSplit]);

  React.useEffect(() => {
    if (open) {
      calculateSchedule();
    }
  }, [open, calculateSchedule]);

  // `form.watch(cb)` appelé en plein rendu créait un abonnement à chaque rendu,
  // jamais résilié. On souscrit une seule fois, et on se désabonne.
  React.useEffect(() => {
    if (!open) return;
    const subscription = form.watch(() => calculateSchedule());
    return () => subscription.unsubscribe();
  }, [open, form, calculateSchedule]);

  const onSubmit = async (data: SplitPaymentFormValues) => {
    if (!subscriptionId) {
      toast({
        title: t("common:error", { defaultValue: "Erreur" }),
        description: t("splitPayment.noSubscription", {
          defaultValue: "Sélectionnez d'abord un abonnement dans la liste des paiements.",
        }),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Enregistrement réel dans `subscription_payment_schedules` : l'ancien
      // `setTimeout` + toast n'écrivait rien.
      const { data: created, error } = await createPaymentSchedule(
        subscriptionId,
        paymentSchedule.map((item) => ({
          installmentNumber: item.installmentNumber,
          dueDate: format(item.dueDate, "yyyy-MM-dd"),
          amount: item.amount,
        }))
      );

      if (error || !created) {
        console.error("[SplitPaymentDialog] création de l'échéancier échouée:", error);
        toast({
          title: t("common:error", { defaultValue: "Erreur" }),
          description:
            error?.message ||
            t("splitPayment.setupFailed", { defaultValue: "L'échéancier n'a pas pu être enregistré." }),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("splitPayment.setupComplete"),
        description: `${t("splitPayment.numberOfPayments")}: ${data.numberOfPayments}`,
      });

      onComplete(paymentSchedule);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t("splitPayment.title")}</DialogTitle>
          <DialogDescription>
            {t("splitPayment.description")} {clientName}
          </DialogDescription>
        </DialogHeader>

        {!canSubmit && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950 p-3 text-sm">
            {t("splitPayment.noSubscription", {
              defaultValue: "Sélectionnez d'abord un abonnement dans la liste des paiements.",
            })}
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("splitPayment.frequency")}</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("splitPayment.frequency")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="monthly">{t("splitPayment.monthly")}</SelectItem>
                        <SelectItem value="quarterly">{t("splitPayment.quarterly")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="numberOfPayments"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("splitPayment.numberOfPayments")}</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={2} 
                        max={12} 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="firstPaymentDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t("splitPayment.firstPaymentDate")}</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              "Select a date"
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="mt-6">
              <h3 className="text-md font-medium mb-2">{t("splitPayment.schedule")}</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>{t("splitPayment.dueDate")}</TableHead>
                    <TableHead className="text-right">{t("amount")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentSchedule.map((item) => (
                    <TableRow key={item.installmentNumber}>
                      <TableCell>{t("splitPayment.installment", { number: item.installmentNumber })}</TableCell>
                      <TableCell>{format(item.dueDate, "PPP")}</TableCell>
                      <TableCell className="text-right">{item.amount}TND</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-medium">
                    <TableCell colSpan={2}>{t("invoice.total")}</TableCell>
                    <TableCell className="text-right">{amountToSplit}TND</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t("common:cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !canSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("common:processing")}
                  </>
                ) : (
                  <>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    {t("splitPayment.title")}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
